import type { Product, Variant } from '@scayle/storefront-nuxt'
import type {
  FitMixInstance,
  FitMixLensMaterial,
  FitMixParameters,
} from '~/types/fitmix'
import { useGlasses } from './pdp/useGlasses'

export function useRxVTO(props: {
  product: Product
  variant: Variant
  containerId?: string
  lensColor?: Ref<string | undefined>
}) {
  const instance = useNuxtApp()
  const noTintingMaterial = 'NO_TINTING' as const
  const { $currentShop, $fittingBox } = instance
  const {
    trackVTOFittingBoxOpen,
    trackVTOFittingBoxClose,
    trackVTOFittingBoxScreenshot,
  } = useTrackingEvents()

  const scriptReady = computed(() => $fittingBox.scriptReady.value)
  const containerReady = ref(false)
  const fitMixReady = ref(false)
  const fitMixCreated = ref(false)
  const fitmixInstance = ref<FitMixInstance>()
  const lensColor = toRef(props.lensColor)
  const isScreenshotAllowed = ref(false)
  const isScreenshotInProgress = ref(false)

  const activeEan = computed(() =>
    props.variant ? getVirtualTryOnEan(props.variant) : undefined,
  )

  const { name: productName } = useProductBaseInfo(props.product)
  const { productColor } = useGlasses(props.product)

  const filename = computed(
    () => `${productName.value}-${productColor.value}.png`,
  )

  const vtoApiKey = computed(() => $fittingBox.vtoApiKey.value)

  const initialize = () => {
    if (import.meta.server) return

    if (
      !scriptReady.value ||
      !vtoApiKey.value ||
      !activeEan.value ||
      !props.containerId
    ) {
      return
    }

    const container = document.getElementById(props.containerId)
    if (!container) return

    const params: FitMixParameters = {
      apiKey: vtoApiKey.value,
      lang: getIsoLanguage(toValue($currentShop).locale),
      frame: activeEan.value,
      uiConfiguration: {
        apiKeyIssueMessage: true,
        frameIssueMessage: true,
        liveCameraAccessDenied: true,
        liveDetectionFailure: true,
        liveLensesAnimations: true,
        liveLensesAnimationsActive: true,
        livePhotoButton: false,
        liveRelaunchDetectionButton: false,
        loadingIndicator: true,
        networkIssueMessage: true,
        photoLiveButton: false,
        photoRenderError: false,
        photoWelcomeScreen: true,
      },
      onSnapshot: handleSnapshot,
    }

    try {
      fitMixCreated.value = true
      fitmixInstance.value = $fittingBox.createFitMixWidget(
        props.containerId,
        params,
        (ready) => {
          fitMixReady.value = ready
        },
      )
    } catch (error) {
      console.error('VTO: Error creating widget:', error)
      fitMixCreated.value = false
      fitMixReady.value = false
    }
  }

  const setupInitialization = (
    containerRef: Ref<HTMLDivElement | undefined>,
  ) => {
    const scope = effectScope()
    tryOnScopeDispose(() => scope.stop())

    scope.run(() => {
      if (import.meta.server) {
        return
      }

      watch(
        containerRef,
        (newContainer) => {
          containerReady.value = Boolean(newContainer)
          if (
            containerReady.value &&
            scriptReady.value &&
            !fitMixCreated.value
          ) {
            initialize()
          }
        },
        { immediate: true },
      )

      watch(
        scriptReady,
        (ready) => {
          if (ready && containerReady.value && !fitMixCreated.value) {
            initialize()
          }
        },
        { immediate: true },
      )

      watch(lensColor, (newColor) => {
        if (!fitmixInstance.value) return

        if (newColor === undefined && isGlasses(props.product)) {
          fitmixInstance.value.setLensMaterial(noTintingMaterial)
        } else if (newColor === undefined) {
          fitmixInstance.value.restoreLensDefaultMaterial()
        } else {
          fitmixInstance.value.setLensMaterial(newColor as FitMixLensMaterial)
        }
      })
    })

    return {
      stop: () => scope.stop(),
      cleanup: () => {
        if (props.containerId) {
          $fittingBox.resetSession(props.containerId)
          fitMixCreated.value = false
          fitMixReady.value = false
          fitmixInstance.value = undefined
        }
      },
    }
  }

  const handleSnapshot = (data: { dataUrl: string }) => {
    if (!isScreenshotAllowed.value) {
      return
    }

    const link = document.createElement('a')
    link.href = data.dataUrl
    link.download = filename.value
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)

    // Reset screenshot states
    isScreenshotAllowed.value = false
    isScreenshotInProgress.value = false
  }

  const handleScreenshot = () => {
    isScreenshotAllowed.value = true
    isScreenshotInProgress.value = true
    fitmixInstance.value?.getSnapshot()

    trackVTOFittingBoxScreenshot({
      productDetails: props.product,
      variant: props.variant,
      element: { id: 'screenshot', label: 'Screenshot' },
    })
  }

  const openVirtualWindow = (event: Event) => {
    if (props.variant) {
      trackVTOFittingBoxOpen({
        productDetails: props.product,
        variant: props.variant,
        element: event?.target as HTMLButtonElement,
      })
    }

    if (fitmixInstance.value && activeEan.value) {
      fitmixInstance.value.setFrame(activeEan.value)
      fitmixInstance.value.startVto('live')

      if (lensColor.value) {
        // This is a workaround to avoid the lens material (specifically NO_TINTING) not being applied correctly
        // when the VTO is opened.
        fitmixInstance.value?.setLensMaterial('FIELMANN_Brown')
        setTimeout(() => {
          fitmixInstance.value?.setLensMaterial(
            lensColor.value as FitMixLensMaterial,
          )
        }, 200)
      }
    }
  }

  const closeVirtualWindow = () => {
    fitmixInstance.value?.resetSession()
    fitmixInstance.value?.stopVto()

    if (props.variant) {
      trackVTOFittingBoxClose({
        productDetails: props.product,
        variant: props.variant,
        element: { id: 'close-button', label: 'Close' },
      })
    }
  }

  return {
    fitMixReady,
    fitmixInstance,
    isScreenshotInProgress,
    initialize,
    setupInitialization,
    openVirtualWindow,
    closeVirtualWindow,
    handleScreenshot,
  }
}
